import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoNotificationsOutline } from 'react-icons/io5';
import i18next from 'i18next';
import OneSignal from 'react-onesignal';
import './PushPrompt.css';

const DISMISS_KEY = 'os_prompt_dismissed';

const PushPrompt = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate); // keep latest navigate for the once-registered listener
  navRef.current = navigate;
  const initedRef = useRef(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;

    const onClick = (event) => {
      const url = event?.result?.url || event?.notification?.launchURL;
      if (!url) return;

      // Custom app deep-link scheme (e.g. orientaldaily://news/.../828325/) -> the website.
      // The service worker's clients.openWindow() only opens http(s), so a custom scheme
      // silently fails; rewrite it to the orientaldaily.com.my web URL and open it ourselves.
      if (url.startsWith('orientaldaily://')) {
        const webUrl = 'https://www.orientaldaily.com.my/' + url.slice('orientaldaily://'.length);
        const win = window.open(webUrl, '_blank', 'noopener,noreferrer');
        if (!win) window.location.href = webUrl; // popup blocked -> navigate current tab
        return;
      }

      const appOrigin = window.location.origin;
      let parsed;
      try { parsed = new URL(url, appOrigin); } catch { return; }
      if (parsed.origin === appOrigin) {
        // internal app route -> smooth SPA navigation (strip BrowserRouter basename)
        const base = process.env.PUBLIC_URL || '';
        let path = parsed.pathname;
        if (base && path.startsWith(base)) path = path.slice(base.length) || '/';
        navRef.current(path + parsed.search + parsed.hash);
      }
      // external (cross-origin) -> handled by OneSignal default (opens in a new tab); no-op here
    };

    OneSignal.init({
      appId: '5b203b5b-9d70-4a46-b157-47d1537f821a',
      safari_web_id: 'web.onesignal.auto.5dcf04a7-d9b5-4793-8717-b5ec1870e3bb',
      notifyButton: { enable: false }, // custom banner instead of the bell
      allowLocalhostAsSecureOrigin: true,
      notificationClickHandlerMatch: 'origin',
      notificationClickHandlerAction: 'focus', // focus existing tab so we own internal nav
    })
      .then(() => {
        OneSignal.Notifications.addEventListener('click', onClick);
        const granted = OneSignal.Notifications.permission === true;
        const denied = OneSignal.Notifications.permissionNative === 'denied';
        const dismissed = localStorage.getItem(DISMISS_KEY) === '1';
        if (!granted && !denied && !dismissed) setShow(true);
      })
      .catch((e) => console.error('OneSignal init failed', e));
  }, []);

  const subscribe = async () => {
    try {
      const granted = await OneSignal.Notifications.requestPermission();
      if (granted && OneSignal.User.PushSubscription.optedIn === false) {
        await OneSignal.User.PushSubscription.optIn();
      }
    } catch (e) { console.error('OneSignal subscribe failed', e); }
    setShow(false);
  };

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, '1'); setShow(false); };

  if (!show) return null;
  return (
    <div className={`push-prompt ${show ? 'is-open' : ''}`} role="dialog" aria-live="polite">
      <div className="push-prompt__text">
        <IoNotificationsOutline className="push-prompt__icon" aria-hidden="true" />
        <div>
          <div className="push-prompt__title">{i18next.t('订阅最新森美兰州选新闻推送')}</div>
          <div className="push-prompt__sub">{i18next.t('第一时间接收开票成绩与重要新闻')}</div>
        </div>
      </div>
      <div className="push-prompt__actions">
        <button className="push-prompt__btn push-prompt__btn--primary" onClick={subscribe}>
          {i18next.t('允许通知')}
        </button>
        <button className="push-prompt__btn" onClick={dismiss}>{i18next.t('暂不')}</button>
      </div>
    </div>
  );
};

export default PushPrompt;
